<template>
  <Preloader v-if="localLoading" />
  <VRow v-else class="h-100">
    <VCol cols="12" class="pt-0 pt-md-2 d-flex flex-column h-100">
      <VacancyStepTitle
        v-if="vacancy"
        :progress="60"
        :vacancy="vacancy"
        :step="3"
      >
        {{ $t('step') }}
      </VacancyStepTitle>
      <TestForm
        :vacancy-id="vacancyId"
        :finish-button-text="$t('next')"
        @finish="next"
        @goBack="back"
      />
    </VCol>
  </VRow>
</template>

<script>
import {
  wizardSteps,
} from '@/enums';
import Preloader from '@/components/Preloader.vue';
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import Wizard from '@/orm/models/Wizard/Wizard';
import {
  ref, toRefs, onMounted, computed,
} from '@vue/composition-api';
import useVacancies from '@/compositions/useVacancies';
import useValidation from '@/compositions/useValidation';
import TestForm from '@/components/Test/TestForm.vue';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'VacancyStepTest',
  metaInfo() {
    return {
      title: this.$t('step'),
    };
  },
  components: {
    TestForm,
    VacancyStepTitle,
    Preloader,
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  setup(props, { root }) {
    const { vacancyId } = toRefs(props);
    const {
      withLoading,
    } = useValidation();
    const localLoading = ref(true);
    const { t } = useI18n()
    const { getVacancyById, fetchById } = useVacancies();
    const vacancy = computed(() => getVacancyById(vacancyId.value));
    
    const isCompiling = computed(() => {
      const currentVacancy = vacancy.value;
      return (currentVacancy?.isAiInterviewCompiling || currentVacancy?.isAiInterviewRecompiling) && !currentVacancy.isAiInterviewCompiled;
    });

    function setState() {
      Wizard
        .setVacancyId(vacancyId.value)
        .setWizardStep(wizardSteps.TESTING);
    }

    async function prepare() {
      setState();
      await withLoading(async () => {
        await Promise.all([
          Wizard.fetchData(),
          Wizard.saveWizardState(),
        ]);
      }, 'loading');
    }

    async function next() {
      await withLoading(async () => {
        await Wizard.saveWizardState();
        await Wizard.pushNextRoute(root.$router);
      }, 'loading');
    }

    function back() {
      Wizard.pushPrevRoute(root.$router);
    }

    onMounted(async () => {
      localLoading.value = true;
      await fetchById(vacancyId.value);
      localLoading.value = false;
    });

    return {
      vacancy,
      prepare,
      localLoading,
      next,
      back,
      isCompiling,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.prepare());
  },
  beforeRouteUpdate(to, from, next) {
    this.prepare();
    next();
  },
};
</script>

<style scoped lang="scss">
.vacancy-step-testing {
  &__title {
    color: #000000;
    font-size: 18px;
    line-height: 28px;
    max-width: 165px;
    @media screen and (max-width: 864px) {
      margin-bottom: 16px;
    }
  }
  &__choice-type {
    @media screen and (max-width: 864px) {
      flex-direction: column;
    }
  }
  &__choice-score-type {
    @media screen and (max-width: 864px) {
      flex-direction: column;
    }
    &_radio-group {
      width: 70% !important;
      @media screen and (max-width: 864px) {
        width: 100% !important;
      }
    }
  }
  &__q {
    width: 100%;
    margin-top: 20px;
    @media screen and (max-width: 599px) {
      margin-top: 40px;
    }
  }
  &__radio-label {
    max-width: 75%;
    margin-left: 33px;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.25px;
    color: #333333;
    margin-top: 4px;
  }
  &__radio-input-block {
    margin: 10px 0 0 33px;
    display: flex;
    span {
      margin: 7px 20px 0 5px;
    }
    &_points {
      font-size: 14px;
      margin-top: 7px;
    }
  }
  &__radio-input {
    max-width: 70px;
  }
  &__radio-input .v-input__control {
    display: none !important;
  }
  &__radio-warning {
    font-weight: 700;
    max-width: 75%;
    font-size: 14px;
    margin: 0px 0 12px 33px
  }
  &__points-info {
    color: #000;
    font-size: 18px;
    line-height: 28px;
    margin-top: 30px;
    display: flex;
    margin-left: -13px;
    @media screen and (max-width: 768px) {
      flex-direction: column;
      margin-top: 0;
    }
    &_names, &_inputs {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    &_names {
      margin-right: 40px;
      @media screen and (max-width: 768px) {
        margin-right: 0;
        margin-bottom: 15px;
      }
    }
    &_names div {
      height: 42px;
      display: flex;
      align-items: center;
    }
    &_time {
      margin: 0 15px;
      max-width: 80px;
    }
    &_inputs {
      max-width: 80px;
      font-size: 16px;
      line-height: 16px;
      div {
        display: flex;
        align-items: center;
        font-size: 14px;
      }
    }
    &_inputs div {
      > input:nth-child(1) {
        margin: 0 15px 0 0;
      }
    }
  }
}

.add-question-block {
  margin-top: 10px;
  margin-left: -13px;
}

input {
  border: 1px solid #C2C2C2;
  border-radius: 4px;
  padding: 6px 12px;
  background-color: #fff;
  outline: none;
}

.required-field {
  &::after {
    content: "*";
    color: red;
    font-size: 12px;
    margin-left: 2px;
  }
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
}

input[type='number'],
input[type="number"]:hover,
input[type="number"]:focus {
    appearance: none;
    -moz-appearance: textfield;
}

.v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
  margin-bottom: 0
}

.v-text-field::v-deep {
  .v-input__control {
    .v-text-field__details {
      min-width: 240px !important;
      padding: 0;
      margin: 5px 0;
    }
  }
}

::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.v-file-input {
  max-width: 220px !important;
}

.v-radio::v-deep {
  .v-label {
    color: #333333 !important;
  }
}

.v-input--checkbox::v-deep {
  .v-input__control {
    .v-input__slot {
      margin-bottom: 0;
    }
    .v-messages {
      display: none;
    }
  }
}
.h-100 {
  height: 100%;
}
</style>

<i18n locale="en" lang="yaml">
step: Choose an interview template
next: Next
vacancyCreated: Job created
</i18n>

<i18n locale="ru" lang="yaml">
step: Тестирование кандидата
next: Далее
vacancyCreated: Вакансия создана
</i18n>
